import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { CssBaseline, CircularProgress, Box } from '@mui/material';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminLayout from './components/Layout/AdminLayout';
import AgentLayout from './components/Layout/AgentLayout';

// Auth Pages
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import PasswordRecovery from './pages/Auth/PasswordRecovery';

// Dashboard
import Dashboard from './pages/Dashboard';

// Pages
import Payments from './pages/Payments';
import Transacoes from './pages/Transacoes';
import RealTimePayments from './pages/RealTimePayments';
import PagaliShop from './pages/PagaliShop';
import Users from './pages/Management/Users';
import Drivers from './pages/Management/Drivers';
import Clients from './pages/Management/Clients';
import Entities from './pages/Management/Entities';
import Advertising from './pages/Management/Advertising';
import CniVerifications from './pages/Management/CniVerifications';
import Onboarding from './pages/Management/Onboarding';
import Billing from './pages/Billing';
import Services from './pages/Config/Services';
import Logs from './pages/Config/Logs';
import Profiles from './pages/Settings/Profiles';
import Banks from './pages/Settings/Banks';
import SispEntities from './pages/Config/SispEntities';
import SispServiceAssociations from './pages/Config/SispServiceAssociations';
import LyraAdmin from './pages/Config/Lyra';
import BillingTariffs from './pages/Config/BillingTariffs';
import OAuthClients from './pages/Config/OAuthClients';
import Invoices from './pages/Config/Invoices';
import ApiDocumentation from './pages/Config/Api';
import ApiKeys from './pages/Config/ApiKeys';
import PaymentLimits from './pages/Config/PaymentLimits';
import DeleteUser from './pages/Config/DeleteUser';
import Types from './pages/Settings/Types';
import ServiceTypes from './pages/Settings/ServiceTypes';
import Languages from './pages/Settings/Languages';
import AppStrings from './pages/Settings/AppStrings';
import HomeCards from './pages/Settings/HomeCards';
import PaymentMethods from './pages/Settings/PaymentMethods';
import BillingTaxes from './pages/Settings/BillingTaxes';
import BillingStates from './pages/Settings/BillingStates';
import Reports from './pages/Reports';
import BI from './pages/BI';
import Notifications from './pages/Notifications';
import Feeds from './pages/Feeds';
import Profile from './pages/Profile';
import Posts from './pages/Settings/Posts';
import ServiceCategories from './pages/Settings/ServiceCategories';
import BillingPeriods from './pages/Settings/BillingPeriods';
import PosSisp from './pages/Settings/PosSisp';
import TopUpRates from './pages/Settings/TopUpRates';
import Numerario from './pages/Reports/Numerario';
import PosTerminais from './pages/POS/Terminais';
import PosFechos from './pages/POS/Fechos';
import PosTransacoes from './pages/POS/Transacoes';
import PosRelatorios from './pages/POS/Relatorios';
import LedgerPage from './pages/Ledger';
import ContratosPage from './pages/Contratos';
import LiquidacoesPage from './pages/Financeiro/Liquidacoes';
import CorsOrigins from './pages/Settings/CorsOrigins';
import SispTokenization from './pages/Settings/SispTokenization';
import CardTypes from './pages/Settings/CardTypes';
import SispBilling from './pages/Billing/SispBilling';
import SispLogs from './pages/Config/SispLogs';
import Sms from './pages/Settings/Sms';
import Email from './pages/Settings/Email';
import ShoppingPlans from './pages/PagaliShop/ShoppingPlans';
import StoreTypes from './pages/PagaliShop/StoreTypes';
import Stores from './pages/PagaliShop/Stores';
import Categories from './pages/PagaliShop/Categories';
import Orders from './pages/PagaliShop/Orders';
import OrderDetails from './pages/PagaliShop/OrderDetails';
import StoreDashboard from './pages/PagaliShop/StoreDashboard';
import Promotions from './pages/PagaliShop/Promotions';
import Inventory from './pages/PagaliShop/Inventory';
import Reviews from './pages/PagaliShop/Reviews';
import Tarifario from './pages/PagaliShop/Tarifario';
import DigitalProducts from './pages/PagaliShop/DigitalProducts';
import FoodBanners from './pages/PagaliFood/Banners';
import FoodRestaurantes from './pages/PagaliFood/Restaurantes';
import FoodTaxaEntrega from './pages/PagaliFood/TaxaEntrega';
import ShopSetup from './pages/Settings/ShopSetup';
import EntityGroups from './pages/Management/EntityGroups';
import PaymentPages from './pages/Management/PaymentPages';
import Permissions from './pages/Management/Permissions';
import Colaboradores from './pages/Management/Colaboradores';
import Events from './pages/Management/Events';
import Plans from './pages/Management/Plans';
import PlanosConfig from './pages/Management/PlanosConfig';
import Donations from './pages/Management/Donations';
import EmailMigration from './pages/Management/EmailMigration';
import FraudAlerts from './pages/Management/FraudAlerts';
import Maintenance from './pages/Management/Maintenance';
import SISPPayments from './pages/Payments/SISP';
import TransactionStatus from './pages/Payments/SISP/TransactionStatus';
import PosVendas from './pages/Payments/SISP/PosVendas';
import TokenPayments from './pages/Payments/Tokens';
import LeadsEntidades from './pages/Management/LeadsEntidades';
import ReconciliacaoSISP from './pages/Payments/SISP/Reconciliacao';
import SispTokens from './pages/Config/SispTokens';
import WalletStats from './pages/WalletStats';
import BcnReport from './pages/BcnReport';
import ReportsFalhas from './pages/Reports/Falhas';
import CashlessAdmin, { ABAS } from './pages/CashlessAdmin';
import Hub from './pages/Hub';
import PagaliVoucher from './pages/PagaliVoucher';
import Cashback from './pages/Cashback';
import CashbackComercial from './pages/CashbackComercial';
import PagaMeAdmin from './pages/PagaMe';
import ChatToPay from './pages/ChatToPay';
import INPSAdmin from './pages/INPS';
import Agentes from './pages/Management/Agentes';
import AgenteDetails from './pages/Management/Agentes/Details';
import AgentesComissoes from './pages/Management/Agentes/Comissoes';

// Agent Portal Pages
import AgenteDashboard from './pages/Agente/Dashboard';
import AgentePagar from './pages/Agente/Pagar';
import AgenteTransacoes from './pages/Agente/Transacoes';
import AgenteComissoes from './pages/Agente/Comissoes';
import AgenteServicos from './pages/Agente/Servicos';
import AgenteClientes from './pages/Agente/Clientes';

// EventsCV Pages
import EventsCVDashboard from './pages/EventsCV';
import EventsCVEvents from './pages/EventsCV/Events';
import EventsCVTransferencias from './pages/EventsCV/Transferencias';
import EventsCVChat from './pages/EventsCV/Chat';
import EventsCVConferencias from './pages/EventsCV/Conferencias';

// iWalkCV Pages
import IWalkCVDashboard from './pages/IWalkCV';
import IWalkCVOperadores from './pages/IWalkCV/Operadores';
import IWalkCVGuias from './pages/IWalkCV/Guias';
import IWalkCVExperiencias from './pages/IWalkCV/Experiencias';
import IWalkCVReservas from './pages/IWalkCV/Reservas';
import IWalkCVCmsHeroSlides from './pages/IWalkCV/CmsHeroSlides';
import IWalkCVIlhas from './pages/IWalkCV/Ilhas';
import IWalkCVCategorias from './pages/IWalkCV/Categorias';
import IWalkCVAvaliacoes from './pages/IWalkCV/Avaliacoes';
import IWalkCVDisponibilidades from './pages/IWalkCV/Disponibilidades';

// Cantinas Pages — Cantinas Escolares
import CantinasAdminDashboard from './pages/Cantinas';
import CantinasEscola from './pages/Cantinas/Escola';

// Pagali Junior — Gestão de contas de jovens
import JuniorAdmin from './pages/Junior';
import SavingsAdmin from './pages/Savings';

// Corporate Portal — Portal RH da empresa (role PFENT)
import CorporatePortalDashboard from './pages/CorporatePortal';
import CorporatePortalEmployees from './pages/CorporatePortal/Employees';
import CorporatePortalDistributions from './pages/CorporatePortal/Distributions';
import CorporatePortalAnalytics from './pages/CorporatePortal/Analytics';

// Corporate B2B — Bracelets NFC multi-tenant
import CorporateDashboard from './pages/Corporate';
import CorporateEmpresaDetail from './pages/Corporate/EmpresaDetail';
import CorporateRewardDistributions from './pages/Corporate/RewardDistributions';
import CorporateBracelets from './pages/Corporate/Bracelets';
import CorporateEmployees from './pages/Corporate/Employees';

// NumisHub — Super Admin
import NumisHubAdmin from './pages/NumisHub';
import NumisHubTenantPage from './pages/NumisHub/TenantPage';

// WAG Pages — Sistema de Gestão de Atendimento
import WAGDashboard from './pages/WAG';
import WAGAgencias from './pages/WAG/Agencias';
import WAGServicos from './pages/WAG/Servicos';
import WAGBalcoes from './pages/WAG/Balcoes';
import WAGOperadores from './pages/WAG/Operadores';
import WAGDisplay from './pages/WAG/Display';
import WAGBilling from './pages/WAG/Billing';

// Banking Pages
import {
  BankingTransfers,
  BankingCredit,
  BankingKYC,
  BankingAnalytics,
  BankingOpenBanking,
  BankingScoring,
  BankingRemessas,
  BankingBCN,
} from './pages/Banking';

// CMS Pages
import CMSDashboard from './pages/CMS';
import CMSPages from './pages/CMS/Pages';
import CMSPageEditor from './pages/CMS/Pages/Editor';
import BlogPosts from './pages/CMS/Blog/Posts';
import BlogPostEditor from './pages/CMS/Blog/PostEditor';
import BlogCategories from './pages/CMS/Blog/Categories';
import BlogAuthors from './pages/CMS/Blog/Authors';
import CMSTestimonials from './pages/CMS/Testimonials';
import CMSParceiros from './pages/CMS/Parceiros';
import CMSFAQ from './pages/CMS/FAQ';
import CMSFooter from './pages/CMS/Footer';
import CMSSettings from './pages/CMS/Settings';
import CMSMediaPage from './pages/CMS/Media';
import CMSTranslations from './pages/CMS/Translations';
import LoginMessages from './pages/CMS/LoginMessages';

// Marketing Pages
import MarketingCampaigns from './pages/Marketing/Campaigns';
import MarketingComunicacao from './pages/Marketing/Comunicacao';
import MarketingEngagement from './pages/Marketing/Engagement';
import CashbackRotativo from './pages/Marketing/CashbackRotativo';
import MarketingMissoes from './pages/Marketing/Missoes';
import MarketingReferral from './pages/Marketing/Referral';
import Embaixadores from './pages/Marketing/Embaixadores';
import ParceirosFundadores from './pages/Marketing/ParceirosFundadores';
import MarketingMetricas from './pages/Marketing/Metricas';
import MarketingConteudo from './pages/Marketing/Conteudo';
import MetaAds from './pages/Marketing/MetaAds';
import LeadsSite from './pages/Marketing/LeadsSite';
import PlanoChoque from './pages/Marketing/PlanoChoque';
import HiggsfieldPage from './pages/Marketing/Higgsfield';
import EventosPromo from './pages/Marketing/EventosPromo';
import ContratosComerciante from './pages/Cashless/ContratosComerciante';
import LiquidacaoComerciantes from './pages/Cashless/LiquidacaoComerciantes';
import ExtratoComerciantes from './pages/Financeiro/ExtratoComerciantes';

// Placeholder component for pages not yet implemented
const ComingSoon = ({ title }: { title: string }) => (
  <div style={{ padding: 24 }}>
    <h2>{title}</h2>
    <p style={{ color: '#666' }}>Esta página está em desenvolvimento.</p>
  </div>
);

// Redirect based on user role
const RoleBasedHome = () => {
  const { user, loading } = useAuth();
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>;
  if (user?.role === 'PFAGT') return <Navigate to="/agente/dashboard" replace />;
  return <Navigate to="/dashboard" replace />;
};

function App() {
  return (
    <ThemeProvider>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/password-recovery" element={<PasswordRecovery />} />

            {/* Agent Portal Routes */}
            <Route
              path="/agente"
              element={
                <ProtectedRoute allowedRoles={['PFAGT']}>
                  <AgentLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/agente/dashboard" replace />} />
              <Route path="dashboard" element={<AgenteDashboard />} />
              <Route path="pagar" element={<AgentePagar />} />
              <Route path="transacoes" element={<AgenteTransacoes />} />
              <Route path="comissoes" element={<AgenteComissoes />} />
              <Route path="servicos" element={<AgenteServicos />} />
              <Route path="clientes" element={<AgenteClientes />} />
            </Route>

            {/* Protected Routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              {/* Dashboard */}
              <Route index element={<RoleBasedHome />} />
              <Route path="dashboard" element={<Dashboard />} />

              {/* PagaMe */}
              <Route path="pagame" element={<PagaMeAdmin />} />

              {/* Chat-to-Pay — vender por WhatsApp */}
              <Route path="chat-to-pay" element={<ChatToPay />} />

              {/* INPS — contribuintes, suportes e leituras por equipamento */}
              <Route path="inps" element={<INPSAdmin />} />

              {/* Payments */}
              <Route path="payments" element={<Payments />} />
              <Route path="transacoes" element={<Transacoes />} />
              <Route path="payments/sisp" element={<SISPPayments />} />
              <Route path="payments/sisp/transaction-status" element={<TransactionStatus />} />
              <Route path="payments/sisp/pos-vendas" element={<PosVendas />} />
              <Route path="payments/sisp/reconciliacao" element={<ReconciliacaoSISP />} />
              <Route path="payments/tokens" element={<TokenPayments />} />
              <Route path="realtime-payments" element={<RealTimePayments />} />

              {/* Billing */}
              <Route path="billing" element={<Billing />} />
              <Route path="billing/sisp" element={<SispBilling />} />

              {/* Reports */}
              <Route path="reports" element={<Reports />} />
              <Route path="bi" element={<BI />} />
              <Route path="reports/wallet" element={<WalletStats />} />
              <Route path="reports/bcn" element={<BcnReport />} />
              <Route path="reports/falhas" element={<ReportsFalhas />} />
              <Route path="reports/numerario" element={<Numerario />} />

              {/* POS Pagali — o menu existia sem rotas por trás */}
              <Route path="pos/terminais" element={<PosTerminais />} />
              <Route path="pos/fechos" element={<PosFechos />} />
              <Route path="pos/transacoes" element={<PosTransacoes />} />
              <Route path="pos/relatorios" element={<PosRelatorios />} />
              <Route path="ledger" element={<LedgerPage />} />
              <Route path="contratos" element={<ContratosPage />} />
              <Route path="liquidacoes" element={<LiquidacoesPage />} />
              {/* endereço antigo: a bilheteira continua no separador Eventos */}
              <Route path="liquidacao-promotor" element={<Navigate to="/liquidacoes?tab=eventos" replace />} />

              {/* Notifications */}
              <Route path="notifications" element={<Notifications />} />

              {/* Feeds */}
              <Route path="feeds" element={<Feeds />} />

              {/* PagaliShop */}
              <Route path="pagalishop">
                <Route index element={<PagaliShop />} />
                <Route path="dashboard" element={<StoreDashboard />} />
                <Route path="stores" element={<Stores />} />
                <Route path="categories" element={<Categories />} />
                <Route path="orders" element={<Orders />} />
                <Route path="orders/:id" element={<OrderDetails />} />
                <Route path="promotions" element={<Promotions />} />
                <Route path="inventory" element={<Inventory />} />
                <Route path="reviews" element={<Reviews />} />
                <Route path="plans" element={<ShoppingPlans />} />
                <Route path="store-types" element={<StoreTypes />} />
                <Route path="tarifario" element={<Tarifario />} />
                <Route path="digital" element={<DigitalProducts />} />
                <Route path="setup" element={<ShopSetup />} />
              </Route>

              {/* Pagali Food */}
              <Route path="pagalifood">
                <Route path="restaurantes" element={<FoodRestaurantes />} />
                <Route path="entrega" element={<FoodTaxaEntrega />} />
                <Route path="banners" element={<FoodBanners />} />
              </Route>

              {/* Marketing (Comunicação) */}
              <Route path="marketing">
                <Route index element={<MarketingCampaigns />} />
                <Route path="campaigns" element={<MarketingCampaigns />} />
                <Route path="comunicacao" element={<MarketingComunicacao />} />
                <Route path="engagement" element={<MarketingEngagement />} />
                <Route path="cashback-rotativo" element={<CashbackRotativo />} />
                <Route path="missoes" element={<MarketingMissoes />} />
                <Route path="referral" element={<MarketingReferral />} />
                <Route path="parceiros-fundadores" element={<ParceirosFundadores />} />
                <Route path="embaixadores" element={<Embaixadores />} />
                <Route path="conteudo" element={<MarketingConteudo />} />
                <Route path="metricas" element={<MarketingMetricas />} />
                <Route path="meta-ads" element={<MetaAds />} />
                <Route path="leads-site" element={<LeadsSite />} />
                <Route path="plano-choque" element={<PlanoChoque />} />
                <Route path="higgsfield" element={<HiggsfieldPage />} />
                <Route path="eventos-promo" element={<EventosPromo />} />
                <Route path="publicidade" element={<HomeCards />} />
              </Route>

              {/* Pagali Voucher */}
              <Route path="pagali-voucher" element={<PagaliVoucher />} />
              <Route path="cashback" element={<Cashback />} />
              <Route path="cashback-comercial" element={<CashbackComercial />} />

              {/* Hub Pagali */}
              <Route path="hub">
                <Route index element={<Hub />} />
                <Route path="dashboard" element={<Hub />} />
                <Route path="parceiros" element={<Hub />} />
                <Route path="apps" element={<Hub />} />
                <Route path="niveis" element={<Hub />} />
                <Route path="novidades" element={<Hub />} />
                <Route path="recursos" element={<Hub />} />
                <Route path="setup" element={<Hub />} />
              </Route>

              {/* Banking - Serviços Bancários */}
              <Route path="banking">
                <Route index element={<BankingTransfers />} />
                <Route path="transfers" element={<BankingTransfers />} />
                <Route path="bills" element={<Navigate to="/payments" replace />} />
                <Route path="credit" element={<BankingCredit />} />
                <Route path="kyc" element={<BankingKYC />} />
                <Route path="analytics" element={<BankingAnalytics />} />
                <Route path="open-banking" element={<BankingOpenBanking />} />
                <Route path="scoring" element={<BankingScoring />} />
                <Route path="remessas" element={<BankingRemessas />} />
                <Route path="bcn" element={<BankingBCN />} />
              </Route>

              {/* iWalkCV - Experiências Turísticas */}
              <Route path="iwalkcv">
                <Route index element={<IWalkCVDashboard />} />
                <Route path="operadores" element={<IWalkCVOperadores />} />
                <Route path="guias" element={<IWalkCVGuias />} />
                <Route path="experiencias" element={<IWalkCVExperiencias />} />
                <Route path="reservas" element={<IWalkCVReservas />} />
                <Route path="avaliacoes" element={<IWalkCVAvaliacoes />} />
                <Route path="disponibilidades" element={<IWalkCVDisponibilidades />} />
                <Route path="ilhas" element={<IWalkCVIlhas />} />
                <Route path="categorias" element={<IWalkCVCategorias />} />
                <Route path="cms/hero-slides" element={<IWalkCVCmsHeroSlides />} />
              </Route>

              {/* Cantinas — Cantinas Escolares */}
              <Route path="cantinas">
                <Route index element={<CantinasAdminDashboard />} />
                <Route path=":id" element={<CantinasEscola />} />
              </Route>

              {/* Pagali Junior */}
              <Route path="junior" element={<JuniorAdmin />} />
              {/* Desafios de Poupança */}
              <Route path="savings" element={<SavingsAdmin />} />

              {/* Corporate Portal — RH da empresa (PFENT) */}
              <Route path="corporate-portal">
                <Route index element={<CorporatePortalDashboard />} />
                <Route path="employees" element={<CorporatePortalEmployees />} />
                <Route path="distributions" element={<CorporatePortalDistributions />} />
                <Route path="analytics" element={<CorporatePortalAnalytics />} />
              </Route>

              {/* Corporate B2B — Bracelets NFC multi-tenant */}
              <Route path="corporate">
                <Route index element={<CorporateDashboard />} />
                <Route path=":id" element={<CorporateEmpresaDetail />} />
                <Route path="employees" element={<CorporateEmployees />} />
                <Route path="bracelets" element={<CorporateBracelets />} />
                <Route path="rewards" element={<CorporateRewardDistributions />} />
              </Route>

              {/* NumisHub — Super Admin */}
              <Route path="numishub" element={<NumisHubAdmin />} />
              <Route path="numishub/:slug" element={<NumisHubTenantPage />} />

              {/* WAG — Sistema de Gestão de Atendimento */}
              <Route path="wag">
                <Route index element={<WAGDashboard />} />
                <Route path="agencias" element={<WAGAgencias />} />
                <Route path="servicos" element={<WAGServicos />} />
                <Route path="balcoes" element={<WAGBalcoes />} />
                <Route path="operadores" element={<WAGOperadores />} />
                <Route path="display" element={<WAGDisplay />} />
                <Route path="billing" element={<WAGBilling />} />
              </Route>

              {/* EventsCV - Gestão de Eventos */}
              <Route path="eventscv">
                <Route index element={<EventsCVDashboard />} />
                <Route path="events" element={<EventsCVEvents />} />
                <Route path="conferences" element={<EventsCVConferencias />} />
                <Route path="transfers" element={<EventsCVTransferencias />} />
                <Route path="tickets" element={<ComingSoon title="Gestão de Bilhetes" />} />
                <Route path="vendors" element={<ComingSoon title="Vendedores" />} />
                <Route path="wallet" element={<CashlessAdmin />} />
                {/* Aponta para a página que JÁ gere pulseiras, no separador certo.
                    Era um <ComingSoon /> a prometer o que existia ao lado. */}
                <Route path="nfc" element={<CashlessAdmin abaInicial={ABAS.pulseiras} />} />
                <Route path="nfc/stock" element={<CashlessAdmin abaInicial={ABAS.stock} />} />
                <Route path="nfc/onboarding" element={<CashlessAdmin abaInicial={ABAS.onboarding} />} />
                <Route path="liquidacoes" element={<CashlessAdmin abaInicial={ABAS.liquidacoes} />} />
                <Route path="gamification" element={<ComingSoon title="Gamificação" />} />
                <Route path="loyalty" element={<ComingSoon title="Fidelização" />} />
                <Route path="chat" element={<EventsCVChat />} />
                <Route path="attendees" element={<ComingSoon title="Participantes" />} />
                <Route path="analytics" element={<ComingSoon title="Analytics" />} />
              </Route>

              {/* CMS - Gestão de Conteúdos */}
              <Route path="cms">
                <Route index element={<CMSDashboard />} />
                <Route path="pages" element={<CMSPages />} />
                <Route path="pages/:id" element={<CMSPageEditor />} />
                <Route path="blog/posts" element={<BlogPosts />} />
                <Route path="blog/posts/new" element={<BlogPostEditor />} />
                <Route path="blog/posts/:id" element={<BlogPostEditor />} />
                <Route path="blog/categories" element={<BlogCategories />} />
                <Route path="blog/authors" element={<BlogAuthors />} />
                <Route path="testimonials" element={<CMSTestimonials />} />
                <Route path="parceiros" element={<CMSParceiros />} />
                <Route path="faq" element={<CMSFAQ />} />
                <Route path="footer" element={<CMSFooter />} />
                <Route path="settings" element={<CMSSettings />} />
                <Route path="media" element={<CMSMediaPage />} />
                <Route path="translations" element={<CMSTranslations />} />
                <Route path="login-messages" element={<LoginMessages />} />
              </Route>

              {/* Management */}
              <Route path="management">
                <Route path="users" element={<Users />} />
                <Route path="drivers" element={<Drivers />} />
                <Route path="profiles" element={<Profiles />} />
                <Route path="permissions" element={<Permissions />} />
                <Route path="colaboradores" element={<Colaboradores />} />
                <Route path="clients" element={<Clients />} />
                <Route path="entities" element={<Entities />} />
                <Route path="leads-entidades" element={<LeadsEntidades />} />
                <Route path="entity-groups" element={<EntityGroups />} />
                <Route path="payment-pages" element={<PaymentPages />} />
                <Route path="events" element={<Events />} />
                <Route path="plans" element={<Plans />} />
                <Route path="planos-config" element={<PlanosConfig />} />
                <Route path="events/tickets" element={<ComingSoon title="Bilhetes de Eventos" />} />
                <Route path="events/vendors" element={<ComingSoon title="Vendedores de Eventos" />} />
                <Route path="events/wallet" element={<ComingSoon title="Wallet de Eventos" />} />
                <Route path="donations" element={<Donations />} />
                <Route path="advertising" element={<Advertising />} />
                <Route path="advertising/onboarding" element={<Onboarding />} />
                <Route path="email-migration" element={<EmailMigration />} />
                <Route path="fraud-alerts" element={<FraudAlerts />} />
                <Route path="contratos-comerciante" element={<ContratosComerciante />} />
                <Route path="liquidacao-comerciantes" element={<LiquidacaoComerciantes />} />
                <Route path="extrato-comerciantes" element={<ExtratoComerciantes />} />
                <Route path="agentes" element={<Agentes />} />
                <Route path="agentes/:id" element={<AgenteDetails />} />
                <Route path="agentes/comissoes" element={<AgentesComissoes />} />
                <Route path="cni-verifications" element={<CniVerifications />} />
                <Route path="maintenance" element={<Maintenance />} />
              </Route>

              {/* Configuration */}
              <Route path="config">
                <Route path="sisp/entities" element={<SispEntities />} />
                <Route path="sisp/services" element={<SispServiceAssociations />} />
                <Route path="sisp/tokens" element={<SispTokens />} />
                <Route path="services" element={<Services />} />
                <Route path="tariffs" element={<BillingTariffs />} />
                <Route path="oauth" element={<OAuthClients />} />
                <Route path="logs" element={<Logs />} />
                <Route path="lyra" element={<LyraAdmin />} />
                <Route path="invoices" element={<Invoices />} />
                <Route path="api" element={<ApiDocumentation />} />
                <Route path="api-keys" element={<ApiKeys />} />
                <Route path="payment-limits" element={<PaymentLimits />} />
                <Route path="delete-user" element={<DeleteUser />} />
              </Route>

              {/* Settings (Parametrizações) */}
              <Route path="settings">
                <Route path="posts" element={<Posts />} />
                <Route path="types" element={<Types />} />
                <Route path="service-categories" element={<ServiceCategories />} />
                <Route path="service-types" element={<ServiceTypes />} />
                <Route path="billing/taxes" element={<BillingTaxes />} />
                <Route path="billing/states" element={<BillingStates />} />
                <Route path="billing/periods" element={<BillingPeriods />} />
                <Route path="banks" element={<Banks />} />
                <Route path="languages" element={<Languages />} />
                <Route path="app-strings" element={<AppStrings />} />
                <Route path="home-cards" element={<HomeCards />} />
                <Route path="payment-methods" element={<PaymentMethods />} />
                <Route path="sms" element={<Sms />} />
                <Route path="sms-intervals" element={<Navigate to="/settings/sms" replace />} />
                <Route path="twilio" element={<Navigate to="/settings/sms" replace />} />
                <Route path="email" element={<Email />} />
                <Route path="pos-sisp" element={<PosSisp />} />
                <Route path="topup-rates" element={<TopUpRates />} />
                <Route path="cors-origins" element={<CorsOrigins />} />
                <Route path="sisp-logs" element={<SispLogs />} />
                <Route path="sisp-tokenization" element={<SispTokenization />} />
                <Route path="card-types" element={<CardTypes />} />
              </Route>

              {/* Profile */}
              <Route path="profile" element={<Profile />} />

              {/* Legacy routes redirect */}
              <Route path="customers" element={<Navigate to="/management/clients" replace />} />
              <Route path="entities" element={<Navigate to="/management/entities" replace />} />
              <Route path="invoices" element={<Navigate to="/billing" replace />} />
            </Route>

            {/* 404 Not Found */}
            <Route path="*" element={<RoleBasedHome />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
